import { Component, Input, Output, EventEmitter, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { OptimizedScreenAnalyzerService, OptimizedScreenAnalysisResult } from '../../services/optimized-screen-analyzer.service';
import { NzMessageService } from 'ng-zorro-antd/message';

@Component({
  selector: 'app-optimized-auto-screen-generator',
  templateUrl: './optimized-auto-screen-generator.component.html',
  styleUrls: ['./optimized-auto-screen-generator.component.scss'],
  standalone: false
})
export class OptimizedAutoScreenGeneratorComponent implements OnChanges {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() screenGenerated = new EventEmitter<OptimizedScreenAnalysisResult>();

  // Wizard steps
  currentStep = 0;
  steps = [
    { title: 'JSON de Entrada', icon: 'file-text', description: 'Cole o JSON de exemplo' },
    { title: 'Análise Inteligente', icon: 'radar-chart', description: 'Análise otimizada dos dados' },
    { title: 'Configuração Avançada', icon: 'control', description: 'Personalize a geração' },
    { title: 'Geração', icon: 'thunderbolt', description: 'Gerar tela otimizada' }
  ];

  // Input
  jsonInput = '';
  jsonError = '';

  // Analysis result
  analysisResult: OptimizedScreenAnalysisResult | null = null;

  // Configuration - Expandida e mais inteligente
  config = {
    layoutStrategy: 'auto' as 'auto' | 'single-tab' | 'multi-tab' | 'hierarchical',
    enableMasterDetail: true,
    autoGenerateForms: true,
    enableCRUD: true,
    enablePagination: true,
    pageSize: 10,
    enableFilters: true,
    enableSorting: true,
    formLayout: 'horizontal' as 'horizontal' | 'vertical',
    selectedEntities: [] as string[],
    //  NOVOS: Configurações otimizadas
    groupLowImportanceEntities: true, // Agrupar entidades de baixa importância
    collapseComplexFields: true, // Colapsar campos complexos por padrão
    maxTabsLimit: 5, // Limite de tabs para não poluir
    showImportanceIndicators: true, // Mostrar indicadores de importância
    generateReadableLabels: true // Gerar labels mais legíveis
  };

  // UI State
  analysisExpanded: { [entityName: string]: boolean } = {};

  constructor(
    private analyzerService: OptimizedScreenAnalyzerService,
    private message: NzMessageService,
    private cdr: ChangeDetectorRef
  ) {
    
  }

  /**
   * Detecta mudanças no input visible
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue === true) {
    
      this.initializeModal();
    }
  }

  /**
   * Inicializa o modal
   */
  private initializeModal() {
    this.currentStep = 0;
    this.jsonInput = '';
    this.jsonError = '';
    this.analysisResult = null;
    this.analysisExpanded = {};
    
    
    
    this.cdr.detectChanges();
  }

  /**
   * Abre o modal (método público para uso externo)
   */
  open() {    
    this.visible = true;
    this.visibleChange.emit(true);
    this.initializeModal();
  }

  /**
   * Fecha o modal e reseta o estado
   */
  close() {
    this.visible = false;
    this.visibleChange.emit(false);
    
    setTimeout(() => {
      this.currentStep = 0;
      this.jsonInput = '';
      this.jsonError = '';
      this.analysisResult = null;
      this.resetConfig();
      this.cdr.markForCheck();
    }, 300);
  }

  /**
   * Reseta configuração para padrão
   */
  private resetConfig() {
    this.config = {
      layoutStrategy: 'auto',
      enableMasterDetail: true,
      autoGenerateForms: true,
      enableCRUD: true,
      enablePagination: true,
      pageSize: 10,
      enableFilters: true,
      enableSorting: true,
      formLayout: 'horizontal',
      selectedEntities: [],
      groupLowImportanceEntities: true,
      collapseComplexFields: true,
      maxTabsLimit: 5,
      showImportanceIndicators: true,
      generateReadableLabels: true
    };
  }

  /**
   * Próximo passo
   */
  next() {
    if (this.currentStep === 0) {
      if (!this.validateJSON()) return;
      this.analyzeJSON();
    }

    if (this.currentStep === 1 && this.analysisResult) {
      // Auto-selecionar entidades por padrão
      if (this.config.selectedEntities.length === 0) {
        this.config.selectedEntities = this.analysisResult.entities.map(e => e.name);
      }
    }

    this.currentStep++;
    this.cdr.markForCheck();
  }

  /**
   * Passo anterior
   */
  previous() {
    this.currentStep--;
    this.cdr.markForCheck();
  }

  /**
   * Valida JSON
   */
  private validateJSON(): boolean {
    this.jsonError = '';

    if (!this.jsonInput.trim()) {
      this.jsonError = 'Por favor, cole um JSON de exemplo';
      return false;
    }

    try {
      const sanitized = this.sanitizeJSON(this.jsonInput);
      JSON.parse(sanitized);
      return true;
    } catch (error) {
      this.jsonError = 'JSON inválido: ' + (error as Error).message;
      return false;
    }
  }

  /**
   * Sanitiza JSON
   */
  private sanitizeJSON(jsonString: string): string {
    let sanitized = jsonString.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
    sanitized = sanitized.replace(/\n/g, ' ');
    sanitized = sanitized.replace(/\r/g, '');
    sanitized = sanitized.replace(/\t/g, ' ');
    sanitized = sanitized.replace(/  +/g, ' ');
    return sanitized;
  }

  /**
   * Analisa JSON com serviço otimizado
   */
  private analyzeJSON() {
    try {
      const sanitized = this.sanitizeJSON(this.jsonInput);
      const json = JSON.parse(sanitized);
      
      this.message.loading('Analisando com algoritmo otimizado...', { nzDuration: 0 });
      
      // Pequeno delay para UI feedback
      setTimeout(() => {
        this.analysisResult = this.analyzerService.analyzeJSON(json);
        this.config.selectedEntities = this.analysisResult.entities.map(e => e.name);
        
        // Auto-detectar estratégia se em modo auto
        if (this.config.layoutStrategy === 'auto') {
          this.config.layoutStrategy = this.analysisResult.metadata.suggestedStrategy;
        }

        this.message.remove();
        this.message.success('✨ Análise otimizada concluída!');
        
        
        this.cdr.markForCheck();
      }, 500);
      
    } catch (error) {
      this.message.error('Erro ao analisar JSON: ' + (error as Error).message);
      this.currentStep = 0;
      this.cdr.markForCheck();
    }
  }

  /**
   * Gera tela com configuração otimizada
   */
  generate() {
    if (!this.analysisResult) return;

    this.message.loading('⚡ Gerando tela otimizada...', { nzDuration: 0 });

    setTimeout(() => {
      //  FIX: Re-calcular o layout baseado na estratégia escolhida pelo usuário
      let finalStrategy = this.config.layoutStrategy;
      
      // Se 'auto', usar a estratégia sugerida automaticamente
      if (finalStrategy === 'auto') {
        finalStrategy = this.analysisResult!.metadata.suggestedStrategy;
      }
      
      
      
      // Re-gerar o layout com a estratégia escolhida
      const regeneratedLayout = this.analyzerService.generateOptimizedLayout(
        this.analysisResult!.entities,
        finalStrategy as any
      );
      
      // Criar resultado final com o layout correto
      const resultWithConfig = {
        ...this.analysisResult!,
        suggestedLayout: regeneratedLayout,
        metadata: {
          ...this.analysisResult!.metadata,
          suggestedStrategy: finalStrategy
        },
        config: this.config
      };
      
      
      
      this.screenGenerated.emit(resultWithConfig);
      this.message.remove();
      this.message.success('✨ Tela otimizada gerada com sucesso!', { nzDuration: 3000 });
      this.close();
    }, 800);
  }

  /**
   * Carrega JSON de exemplo
   */
  loadSampleJSON(sample: 'shopee' | 'exemplo' | 'simple') {
    const samples: { [key: string]: string } = {
      simple: `{
  "data": {
    "users": [
      {
        "id": 1,
        "name": "João Silva",
        "email": "joao@email.com",
        "active": true,
        "createdAt": "2025-01-15"
      }
    ]
  }
}`,
      shopee: `{
  "response": {
    "item_list": [
      {
        "item_id": 22997684885,
        "item_name": "Kit Whey PRO",
        "item_sku": "11025013",
        "item_status": "NORMAL",
        "create_time": 1722375890,
        "update_time": 1722629279,
        "weight": "1.5",
        "image": {
          "image_ratio": "1:1",
          "image_id_list": [
            { "index": 0, "value": "sg-11134201-7rdyg-lyctjfbmd99k1d" },
            { "index": 1, "value": "br-11134207-7r98o-lycqsn62hj3910" }
          ],
          "image_url_list": [
            { "index": 0, "value": "https://cf.shopee.com.br/file/sg-11134201-7rdyg-lyctjfbmd99k1d" }
          ]
        },
        "logistic_info": [
          {
            "logistic_id": 91003,
            "logistic_name": "Entrega Padrão",
            "is_free": false,
            "enabled": true
          }
        ]
      }
    ]
  }
}`,
      exemplo: `{
  "tela": {
    "logistic_info": [
      {
        "estimated_shipping_fee": 10.83,
        "logistic_id": 91003,
        "is_free": false,
        "logistic_name": "Entrega Padrão",
        "Testando": [
          {
            "estimated_shipping_fee": 10.83,
            "logistic_id": 91003,
            "is_free": false
          }
        ]
      }
    ],
    "componentes": [
      {
        "tipo": "cabecalho",
        "id": "cabecalho_principal",
        "propriedades": {
          "titulo": "DaFábricaSuplementos",
          "subtitulo": "Loja Oficial"
        }
      }
    ]
  }
}`
    };

    this.jsonInput = samples[sample] || samples.simple;
    this.cdr.markForCheck();
  }

  /**
   * Toggle expansão de entidade
   */
  toggleEntityExpansion(entityName: string) {
    this.analysisExpanded[entityName] = !this.analysisExpanded[entityName];
    this.cdr.markForCheck();
  }

  /**
   * Verifica se entidade está expandida
   */
  isEntityExpanded(entityName: string): boolean {
    return !!this.analysisExpanded[entityName];
  }

  /**
   * Retorna ícone baseado na importância
   */
  getImportanceIcon(importance: string): string {
    switch (importance) {
      case 'high': return 'star';
      case 'medium': return 'star';
      case 'low': return 'minus-circle';
      default: return 'question';
    }
  }

  /**
   * Retorna cor baseada na importância
   */
  getImportanceColor(importance: string): string {
    switch (importance) {
      case 'high': return '#52c41a';
      case 'medium': return '#1890ff';
      case 'low': return '#8c8c8c';
      default: return '#d9d9d9';
    }
  }

  /**
   * Retorna tag de complexidade
   */
  getComplexityTag(complexity: number): { text: string; color: string } {
    if (complexity >= 7) return { text: 'Alta', color: 'red' };
    if (complexity >= 4) return { text: 'Média', color: 'orange' };
    return { text: 'Baixa', color: 'green' };
  }

  /**
   * Retorna descrição da estratégia
   */
  getStrategyDescription(strategy: string): string {
    switch (strategy) {
      case 'single-tab':
        return 'Todos os dados em uma única tab - ideal para estruturas simples';
      case 'multi-tab':
        return 'Tabs separadas por entidade - organização por contexto';
      case 'hierarchical':
        return 'Agrupamento inteligente hierárquico - ideal para estruturas complexas';
      default:
        return 'Estratégia automática baseada na análise';
    }
  }

  /**
   * Retorna total de tabs previstas
   */
  get estimatedTabsCount(): number {
    if (!this.analysisResult) return 0;
    return this.analysisResult.suggestedLayout.tabs.length;
  }

  /**
   * Retorna total de panels previstos
   */
  get estimatedPanelsCount(): number {
    if (!this.analysisResult) return 0;
    return this.analysisResult.suggestedLayout.panels.length;
  }
}

